// src/hooks/useSandboxPreview.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { writeSandboxHtml } from "@/lib/writeSandboxHtml";
import { getOrCreateActiveSessionId } from "@/lib/activeSessionId";
import { startDependencyPrewarm } from "@/lib/sandboxBuild";

export type ProjectFile = { name: string; content: string };

export interface SandboxConsoleLog {
  type: "log" | "info" | "warn" | "error";
  message: string;
  timestamp: Date;
}

const MAX_CONSOLE_LOGS = 200;

const htmlKeyFor = (sessionId: string) => `dayone:sandbox:html:${sessionId}`;
const filesKeyFor = (sessionId: string) => `dayone:sandbox:files:${sessionId}`;
const snapshotKeyFor = (sessionId: string) => `dayone:sandbox:snapshot:${sessionId}`;

const SANDBOX_PREFIX = "dayone:sandbox:";

/** Remove old sandbox keys for other sessions to avoid QuotaExceededError. */
function pruneSandboxStorage(currentSessionId: string): void {
  if (typeof localStorage === "undefined") return;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(SANDBOX_PREFIX) && !key.includes(currentSessionId)) {
      toRemove.push(key);
    }
  }
  for (const key of toRemove) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn("[useSandboxPreview] pruneSandboxStorage removeItem failed", key, e);
    }
  }
  if (toRemove.length > 0) {
    console.log("[useSandboxPreview] pruneSandboxStorage removed", toRemove.length, "old keys");
  }
}

/**
 * IMPORTANT:
 * We cannot use <script type="module" src="/__sandbox_entry?..."> because that is a *network request*
 * and React Router cannot serve it. That leads to 404 + blank preview.
 *
 * So we generate a self-contained HTML document and store it under dayone:sandbox:html:${sessionId},
 * then __render.tsx writes it into the iframe document.
 */
function buildStableHtml(files: ProjectFile[], sessionId: string): string {
  const get = (p: string) => files.find((f) => f.name === p)?.content ?? "";
  const fileNames = files.map((f) => f.name).sort();

  // Prefer index.html if provided
  let html = (get("index.html") || "").trim();

  // If index.html exists, we try to make it "safe" for our render pipeline:
  // - remove any references to /__sandbox_entry or /__sandbox_file
  // - avoid module script src that will 404
  if (html) {
    // If their index.html references our old endpoints, strip them out
    html = html.replace(/\/__sandbox_entry\?sessionId=[^"']+/g, "");
    html = html.replace(/\/__sandbox_file\/[^"']+/g, "");

    // If index.html contains a module script pointing to src/main.tsx etc,
    // that will not work without a bundling pipeline. Replace with a friendly message.
    const hasModuleScriptSrc = /<script[^>]+type=["']module["'][^>]+src=["'][^"']+["'][^>]*><\/script>/i.test(
      html
    );

    if (hasModuleScriptSrc) {
      // Replace the body with a stable placeholder that explains what's missing
      return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>DayOne Preview</title>
    <style>
      body { font-family: system-ui; padding: 24px; }
      code { background: rgba(0,0,0,.06); padding: 2px 6px; border-radius: 6px; }
      .muted { opacity: .75; }
      .box { margin-top: 16px; padding: 12px; border: 1px solid rgba(0,0,0,.12); border-radius: 10px; }
      ul { margin: 8px 0 0; padding-left: 18px; }
    </style>
  </head>
  <body>
    <h1 style="margin:0 0 8px;">Preview Pipeline Working ✅</h1>
    <p class="muted" style="margin:0 0 12px;">
      We received an <code>index.html</code>, but it contains a <code>&lt;script type="module" src="..."&gt;</code>
      that requires a JS bundling/runtime step. To prevent blank preview + 404s, DayOne is showing this safe fallback.
    </p>

    <div class="box">
      <div><b>sessionId:</b> <code>${escapeHtml(sessionId)}</code></div>
      <div class="muted" style="margin-top:8px;">
        Next step is to add a real sandbox runtime (client-side bundler) that can execute TSX/React from the file list.
      </div>
    </div>

    <div class="box">
      <div><b>Files received (${fileNames.length}):</b></div>
      <ul>
        ${fileNames.map((n) => `<li><code>${escapeHtml(n)}</code></li>`).join("")}
      </ul>
    </div>
  </body>
</html>`;
    }

    // If no module script src, we can render index.html as-is.
    // Ensure it has a <meta charset> at minimum and a title fallback.
    if (!/<meta\s+charset=/i.test(html)) {
      html = html.replace(
        /<head[^>]*>/i,
        (m) => `${m}\n<meta charset="UTF-8" />`
      );
    }
    if (!/<title>/i.test(html)) {
      html = html.replace(/<head[^>]*>/i, (m) => `${m}\n<title>DayOne Preview</title>`);
    }

    return html;
  }

  // No index.html -> safe fallback
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>DayOne Preview</title>
    <style>
      body { font-family: system-ui; padding: 24px; }
      code { background: rgba(0,0,0,.06); padding: 2px 6px; border-radius: 6px; }
      .muted { opacity: .75; }
      .box { margin-top: 16px; padding: 12px; border: 1px solid rgba(0,0,0,.12); border-radius: 10px; }
      ul { margin: 8px 0 0; padding-left: 18px; }
    </style>
  </head>
  <body>
    <h1 style="margin:0 0 8px;">Preview Pipeline Working ✅</h1>
    <p class="muted" style="margin:0 0 12px;">
      Files were saved, and <code>__render</code> successfully wrote HTML into the preview iframe.
      No runnable <code>index.html</code> was provided.
    </p>

    <div class="box">
      <div><b>sessionId:</b> <code>${escapeHtml(sessionId)}</code></div>
      <div class="muted" style="margin-top:8px;">
        Stored keys:
        <ul>
          <li><code>${escapeHtml(htmlKeyFor(sessionId))}</code></li>
          <li><code>${escapeHtml(filesKeyFor(sessionId))}</code></li>
        </ul>
      </div>
    </div>

    <div class="box">
      <div><b>Files received (${fileNames.length}):</b></div>
      <ul>
        ${fileNames.map((n) => `<li><code>${escapeHtml(n)}</code></li>`).join("")}
      </ul>
    </div>
  </body>
</html>`;
}

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export interface UseSandboxPreviewOptions {
  /** When this changes (e.g. iframe remount), sandbox error is cleared. */
  previewKey?: number;
  /** Session ID from parent (e.g. project id). When set, hook stays in sync so preview URL and writes use the same id. */
  sessionId?: string | null;
}

export function useSandboxPreview(options?: UseSandboxPreviewOptions) {
  const { previewKey, sessionId: sessionIdFromParent } = options ?? {};
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() =>
    getOrCreateActiveSessionId(sessionIdFromParent ?? null)
  );
  const [consoleLogs, setConsoleLogs] = useState<SandboxConsoleLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasSnapshot, setHasSnapshot] = useState(false);

  // Keep hook sessionId in sync with parent so iframe URL and localStorage key always match
  useEffect(() => {
    if (sessionIdFromParent != null && sessionIdFromParent !== sessionId) {
      const next = getOrCreateActiveSessionId(sessionIdFromParent);
      setSessionId(next);
    }
  }, [sessionIdFromParent]); // eslint-disable-line react-hooks/exhaustive-deps -- only sync when parent sessionId changes

  // Start prewarming CDN dependencies early to reduce preview build time
  useEffect(() => {
    startDependencyPrewarm();
  }, []);

  // Clear error when preview boots/reloads (new session or iframe remount)
  useEffect(() => {
    setError(null);
  }, [sessionId, previewKey]);

  // Listen for console messages and PREVIEW_READY from the sandbox iframe
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data) return;

      if (data.type === "PREVIEW_READY") {
        setError(null);
        return;
      }
      if (data.type !== "dayone:console") return;
      if (data.sessionId !== sessionIdRef.current) return;

      const log: SandboxConsoleLog = {
        type: (data.level || "log") as SandboxConsoleLog["type"],
        message: String(data.message ?? ""),
        timestamp: new Date(data.ts || Date.now()),
      };

      setConsoleLogs((prev) => {
        const next = [...prev, log];
        return next.length > MAX_CONSOLE_LOGS ? next.slice(-MAX_CONSOLE_LOGS) : next;
      });

      if (data.level === "error") {
        const msg = String(data.message ?? "");
        // Do not treat React's createRoot double-mount warning as a fatal runtime error
        if (
          msg.includes("ReactDOMClient.createRoot") &&
          msg.includes("already been passed to createRoot")
        ) {
          return;
        }
        setError(msg);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const previewUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/__preview?sessionId=${encodeURIComponent(sessionId)}`;
  }, [sessionId]);

  const sandboxUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/__sandbox?sessionId=${encodeURIComponent(sessionId)}`;
  }, [sessionId]);

  const runPreview = useCallback(async (files: ProjectFile[], sid: string) => {
    setSessionId(sid);
    setIsRunning(true);
    setConsoleLogs([]);
    setError(null);

    pruneSandboxStorage(sid);

    // Snapshot current files before overwriting (enables one-click revert)
    try {
      const prev = localStorage.getItem(filesKeyFor(sid));
      if (prev) {
        localStorage.setItem(snapshotKeyFor(sid), prev);
        setHasSnapshot(true);
      }
    } catch (e) {
      console.warn("[useSandboxPreview] snapshot save failed", e);
    }

    // Save files list for debugging / future runtime
    const lsKey = filesKeyFor(sid);
    try {
      localStorage.setItem(lsKey, JSON.stringify(files));
      localStorage.setItem("dayone:sandbox:activeSessionId", sid);
      // Clear build state so __render.tsx knows skeleton phase is over
      try { localStorage.removeItem(`dayone:sandbox:buildstate:${sid}`); } catch { /* ignore */ }
      const paths = files.map(f => f.name);
      console.log(`[PREVIEW WRITE] sessionId=${sid} fileCount=${files.length} paths=`, paths.slice(0, 15));
      // DEBUG: Log App.tsx content to verify it's the AI-generated version
      const appFile = files.find(f => f.name === "src/App.tsx");
      if (appFile) {
        console.log("[PREVIEW WRITE] src/App.tsx content snippet:", appFile.content?.slice(0, 200));
      } else {
        console.warn("[PREVIEW WRITE] WARNING: No src/App.tsx found in files!");
      }
    } catch (e) {
      console.warn("[useSandboxPreview] localStorage write failed (files)", e);
    }

    // Build a SAFE html document that never requests /__sandbox_entry
    const html = buildStableHtml(files, sid);

    // Prefer the centralized writer (BroadcastChannel + events + debug)
    writeSandboxHtml(sid, html);

    // NOTE: Do NOT imperatively set iframeRef.current.src here.
    // VibePreviewPanel controls iframe reload via reloadNonce to avoid double-reload.
    // __render.tsx also polls localStorage every 800ms and will detect changes via content hash.

    return true;
  }, []);

  const reloadIframe = useCallback(() => {
    if (!iframeRef.current) return;
    try {
      iframeRef.current.contentWindow?.location.reload();
    } catch {
      const src = iframeRef.current.src.split("&t=")[0];
      iframeRef.current.src = `${src}&t=${Date.now()}`;
    }
  }, []);

  const reloadStorage = useCallback(() => {
    reloadIframe();
  }, [reloadIframe]);

  const openSandboxTab = useCallback(() => {
    if (!sandboxUrl) return;
    window.open(`${sandboxUrl}&t=${Date.now()}`, "_blank", "noopener,noreferrer");
  }, [sandboxUrl]);

  const clearLogs = useCallback(() => {
    setConsoleLogs([]);
    setError(null);
  }, []);

  /** Restore the previous files snapshot into localStorage and clear errors. Returns true if successful. */
  const revertToSnapshot = useCallback((): boolean => {
    const sid = sessionIdRef.current;
    try {
      const snap = localStorage.getItem(snapshotKeyFor(sid));
      if (!snap) return false;
      localStorage.setItem(filesKeyFor(sid), snap);
      setConsoleLogs([]);
      setError(null);
      return true;
    } catch (e) {
      console.warn("[useSandboxPreview] revert failed", e);
      return false;
    }
  }, []);

  return {
    isRunning,
    previewUrl,
    sandboxUrl,
    iframeRef,
    runPreview,
    reloadIframe,
    reloadStorage,
    openSandboxTab,
    consoleLogs,
    error,
    clearLogs,
    hasSnapshot,
    revertToSnapshot,
  };
}
